/** biome-ignore-all lint/style/useNamingConvention: expected */
import type Path from "@core/FileProvider/Path/Path";
import type { Catalog } from "@core/FileStructue/Catalog/Catalog";
import type FileStructure from "@core/FileStructue/FileStructure";
import type { ItemRef } from "@core/FileStructue/Item/ItemRef";
import { ItemType } from "@core/FileStructue/Item/ItemType";
import type Hasher from "@core/Hash/Hasher";
import type ResourceUpdater from "@core/Resource/ResourceUpdater";
import createNewFilePathUtils from "@core/utils/createNewFilePathUtils";
import { RwLock } from "@core/utils/rwlock";
import type { InboxProps } from "@ext/inbox/models/types";
import { addEvent, Level } from "@ext/loggers/opentelemetry";
import type PrivateParserContext from "@ext/markdown/core/Parser/ParserContext/PrivateParserContext";
import { FileStatus } from "@ext/Watchers/model/FileStatus";
import type { JSONContent } from "@tiptap/core";
import type { RenderableTreeNode } from "../../../extensions/markdown/core/render/logic/Markdoc";
import type { TocItem } from "../../../extensions/navigation/article/logic/createTocItems";
import type { Category } from "../Category/Category";
import { Item, type ItemEvents, type ItemProps, NAV_STRUCTURAL_PROPS, type UpdateItemProps } from "../Item/Item";

export type ArticleEvents = ItemEvents;

export type ArticleInitProps<P extends ItemProps> = {
	ref: ItemRef;
	parent: Category;
	content: string | null;
	props: P;
	logicPath: string;

	fs: FileStructure;

	lastModified: number;
	errorCode?: number;
};

export type ArticleProps =
	| ({
			welcome?: boolean;
	  } & ItemProps)
	| InboxProps;

export const ArticlePropsKeys = [
	"title",
	"properties",
	"date",
	"author",
	"description",
	"template",
	"customProperties",
	"quiz",
	"searchPhrases",
] as const;

export class Article<P extends ArticleProps = ArticleProps> extends Item<P> {
	protected _fs: FileStructure;

	protected _parsedContent = RwLock.store<Content>(null);
	protected _content: string | null;
	protected _lastModified: number;
	private _errorCode?: number;

	constructor({ ref, parent, props, logicPath, ...init }: ArticleInitProps<P>) {
		super(ref, parent, props, logicPath);
		this._content = init.content;
		this._errorCode = init.errorCode;
		this._lastModified = init.lastModified;
		this._fs = init.fs;
	}

	get events() {
		return super.events;
	}

	get parsedContent() {
		return this._parsedContent;
	}

	get lastModified() {
		return this._lastModified;
	}

	async getContent(): Promise<string> {
		await this._loadContent();
		const mutableContent = { content: this._content ?? "" };
		await this.events.emit("item-get-content", { item: this, mutableContent });
		return mutableContent.content;
	}

	hasContent(): boolean {
		return !this._props.shouldBeCreated;
	}

	async reloadFromDisk(catalog?: Catalog): Promise<void> {
		this._content = null;
		this._lastModified = Date.now();
		await this._parsedContent.write(() => null);

		if (this.props.shouldBeCreated) return;
		try {
			const raw = await this._fs.fp.read(this._ref.path);
			const parsed = this._fs.parseMarkdown(raw.toString());
			const mutable = { content: "", props: parsed.props };
			if (catalog) await this._fs.events.emit("item-read", { catalog, mutable });
			this._updateProps(mutable.props, NAV_STRUCTURAL_PROPS);
		} catch (e) {
			addEvent("article-refresh-failed", Level.Commands, { path: this._ref.path.value, error: String(e) });
		}
	}

	get type() {
		return ItemType.article;
	}

	get errorCode(): number {
		return this._errorCode;
	}

	set errorCode(value: number) {
		this._errorCode = value;
	}

	async updateContent(content: string, dropParsedContent = true) {
		this._content = content;
		if (dropParsedContent) await this._parsedContent.write(() => null);
		await this._save();
	}

	async rawUpdateContent(rawContent: string, dropParsedContent = true) {
		const { content, props } = this._fs.parseMarkdown(rawContent);
		this._updateProps(props);
		await this.updateContent(content, dropParsedContent);
	}

	getFileName(): string {
		return this._ref.path.name;
	}

	protected _updateProps(props: UpdateItemProps, additionalKeys: readonly string[] = []) {
		const allProps = this._props as Record<string, unknown>;

		for (const key of [...ArticlePropsKeys, ...additionalKeys]) {
			if (!(key in props)) {
				delete allProps[key];
				continue;
			}

			if (Array.isArray(props[key])) {
				if (props[key]?.length) allProps[key] = props[key];
				else delete allProps[key];
			} else allProps[key] = props[key];
		}
	}

	save() {
		return this._save();
	}

	async hash(hash: Hasher, recursive = true) {
		const hasher = await super.hash(hash);
		hasher.hash(this._content ?? "");
		if (recursive) await this.parsedContent.read((p) => p.parsedContext.getResourceManager().hash(hash));
		return hasher;
	}

	protected async _save(renamed?: boolean) {
		delete this._props.shouldBeCreated;
		delete this._props.welcome;
		if (this._content === null)
			await this._loadContent().catch(() => {
				this._content = "";
			});

		await this.events.emit("item-pre-save", {
			item: this,
			mutable: { content: this._content ?? "", props: this._props },
		});
		if (this._props.title?.toString()?.trim()) delete this._props.external;

		await this._fs.saveArticle(this._ref.path, this._content ?? "", this._props);
		this._lastModified = Date.now();
		await this.events.emit("item-changed", { item: this, status: renamed ? FileStatus.new : FileStatus.modified });
	}

	protected override async _updateFilename(fileName: string, resourceUpdater: ResourceUpdater, catalog: Catalog) {
		if (this.getFileName() === fileName) return;
		let path = this._ref.path.getNewName(fileName);
		if (await this._fs.fp.exists(path)) {
			const readdir = await this._fs.fp.getItems(this.ref.path.parentDirectoryPath);
			path = createNewFilePathUtils.create(
				this.ref.path,
				readdir.map((s) => s.path),
				fileName,
			);
		}
		await this._fs.moveArticle(this, path);
		this._lastModified = Date.now();
		const newArticle = await this._getUpdateArticleByProps(path, catalog);
		this._logicPath = newArticle.logicPath;
		this._ref = newArticle.ref;
		this._content = newArticle._content;
		await resourceUpdater.update(this, newArticle);
		await this.parsedContent.write(() => newArticle.parsedContent.read());

		return this;
	}

	protected async _getUpdateArticleByProps(path: Path, catalog: Catalog) {
		return await this._fs.makeArticleByProps(
			path,
			this._props,
			this._content,
			this._parent,
			this._lastModified,
			catalog,
		);
	}

	private async _loadContent(): Promise<void> {
		if (this._content !== null) return;
		if (this.props.shouldBeCreated) return;

		const raw = await this._fs.fp.read(this._ref.path);
		const parsed = this._fs.parseMarkdown(raw.toString());
		this._content = parsed.content;
	}
}

export interface Content {
	tocItems: TocItem[];
	editTree: JSONContent;
	renderTree: RenderableTreeNode;
	parsedContext: PrivateParserContext;
}
